import 'styled-components';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  generatePath,
  Navigate,
} from 'react-router-dom';
import Welcome from '../../pages/Welcome';
import Tutorial from '../../pages/Tutorial';
import Questions from '../../pages/Questions';
import Answers from '../../pages/Answers';
import Residency from '@/pages/Residency';
import RequireResidency from '@/components/RequireResidency';
import AppGlobalStyle from '@keyslabor/shared/containers/AppGlobalStyle';
import AppGlobalStyleOverride from './AppGlobalStyle';
import theme from '@keyslabor/shared/theme';
import Finish from '@/pages/Finish';
import { useEffect } from 'react';
import { routes } from '@/constants';
import { useQuestionsAnswers } from '@/hooks/useQuestionsAnswers';
import ContainerFullPage from '@/components/ContainerFullPage';
import PreloaderFullPage from '@/components/PreloaderFullPage';
import InterviewPreview from '@/pages/InterviewPreview';
import OfferConfirmation from '@/pages/OfferConfirmation';
import './App.css';
import DesktopBlock from '@/components/DesktopBlock/DesktopBlock';
import ApplyJob from '@/pages/ApplyJob/ApplyJob.tsx';
import ApplyJobInAppFinish from '@/pages/ApplyJob/InAppFinish.tsx';
import ClientLocations from '@/pages/ClientLocations/ClientLocations';

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

const outOfInterviewScopeRoutes = [
  routes.interviewPreview,
  routes.offerConfirmation,
  routes.apply,
];

function RedirectToInteractionsConfirmation() {
  const location = useLocation();

  return (
    <Navigate to={routes.interactionsConfirmation + location.search} replace />
  );
}

// The apply form used to live at `/qr-apply/:id/form` (behind a language screen);
// it is now the entry URL `/qr-apply/:id`. Keep the old path working by
// redirecting legacy links, preserving any query string.
function RedirectApplyFormToEntry() {
  const { property_id } = useParams<{ property_id: string }>();
  const { search } = useLocation();

  return (
    <Navigate
      to={generatePath(routes.apply, { property_id: property_id ?? '' }) + search}
      replace
    />
  );
}

/**
 * The interview-scope route table. Pulled out of `App` so it can be rendered
 * on its own in a test — `App` also runs `useQuestionsAnswers` and its
 * loading/error gating, which pulls in far more than the routing itself.
 *
 * `/questions` and `/answers` are the two recording routes; both must stay
 * behind `RequireResidency`, which is the whole point of this gate existing.
 */
export function InterviewScopeRoutes() {
  return (
    <Routes>
      <Route path={routes.root} element={<Welcome />} />
      <Route path={routes.tutorial} element={<Tutorial />} />
      <Route path={routes.residency} element={<Residency />} />
      <Route
        path={routes.questions}
        element={
          <RequireResidency>
            <Questions />
          </RequireResidency>
        }
      />
      <Route
        path={routes.answers}
        element={
          <RequireResidency>
            <Answers />
          </RequireResidency>
        }
      />
      <Route path={routes.finish} element={<Finish />} />
      <Route path="*" element={<Navigate to={routes.root} replace />} />
    </Routes>
  );
}

function App() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(search);
  const debug = !!params?.get?.('debug');
  const turnOffHandleRouting = !!params?.get?.('turn_off_handle_routing');

  const { invitationKey, remoteStatus, isLoading, isError, error } =
    useQuestionsAnswers();

  // [DEBUG] bypass token
  // const isNoToken = false;
  const isNoToken = invitationKey == null;

  // TODO: remove route handling at all
  useEffect(() => {
    if (turnOffHandleRouting) {
      return;
    }
    switch (remoteStatus?.status) {
      // 'start' needs no redirect: `/` renders Welcome, which is where the
      // applicant chooses the interview language before Tutorial.
      // case 'in_progress':
      //   if ([routes.questions, routes.answers].includes(pathname)) {
      //     navigate(routes.questions);
      //   }
      //   break;
      case 'finished':
        if (
          ![
            routes.interviewPreview,
            routes.offerConfirmation,
            routes.interactionsConfirmation,
          ].includes(pathname)
        ) {
          navigate(routes.finish);
        }
        break;
    }
  }, [remoteStatus?.status, turnOffHandleRouting, navigate, pathname]);

  // [DEBUG] Error boundary
  // throw new Error(
  //   'Uncaught error: Error: Something went wrong. Please refresh the page and try again'
  // );
  if (isLoading) {
    return <PreloaderFullPage />;
  }

  if (!isLoading && (isNoToken || isError || !remoteStatus?.status)) {
    return (
      <ContainerFullPage>
        Please provide a valid invitation key
        {debug && (
          <pre>
            <code>{isError ? JSON.stringify(error) : null}</code>
          </pre>
        )}
      </ContainerFullPage>
    );
  }

  const isInterviewScopePage = !outOfInterviewScopeRoutes.includes(pathname);

  return (
    <>
      {isInterviewScopePage ? <DesktopBlock /> : null}
      <InterviewScopeRoutes />
    </>
  );
}

const AppScopeRouting = () => {
  // @ts-expect-error - GlobalStyle type is not compatible with JSX but works
  const appGlobalStyle = <AppGlobalStyle theme={theme} />;
  // @ts-expect-error - GlobalStyle type is not compatible with JSX but works
  const appGlobalStyleOverride = <AppGlobalStyleOverride theme={theme} />;

  const stylesRender = (
    <>
      {appGlobalStyle}
      {appGlobalStyleOverride}
    </>
  );

  return (
    <>
      {stylesRender}
      <ScrollToTop />
      <Routes>
        <Route path={routes.apply} element={<ApplyJob />} />
        <Route path={routes.applyForm} element={<RedirectApplyFormToEntry />} />
        <Route path={routes.applyClient} element={<ClientLocations />} />
        <Route
          path={routes.applyFormFinish}
          element={<ApplyJobInAppFinish />}
        />
        <Route path={routes.interviewPreview} element={<InterviewPreview />} />
        <Route
          path={routes.interactionsConfirmation}
          element={<OfferConfirmation />}
        />
        <Route
          path={routes.offerConfirmation}
          element={<RedirectToInteractionsConfirmation />}
        />
        <Route path="*" element={<App />} />
      </Routes>
    </>
  );
};

export default AppScopeRouting;
